import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadPanel } from "devextreme-react";
import "./SupportDashboard.css";
import { PieChart, Series, Label, Legend, Tooltip } from "devextreme-react/pie-chart";
import notify from "devextreme/ui/notify";
import { useTranslation } from "react-i18next";
import {
    getTicketStats, getSupportUsers, getTickets, getSurveyStats,
    Ticket, SupportUser, TicketStatus, TicketPriority,
    TICKET_STATUS_DEFS, isClosedStatus,
} from "../../../api/support";
import {
    STATUS_TEXT_KEYS,
    STATUS_BADGE as STATUS_COLOR,
    STATUS_DOT as STATUS_BG,
} from "../messages/ticket-constants";

// ─── Lookup ───────────────────────────────────────────────────────────────────

// Schema: 1=high, 2=medium, 3=low
const PRIORITY_TEXT_KEYS: Record<TicketPriority, string> = {
    1: "priorityHigh", 2: "priorityNormal", 3: "priorityLow",
};
const PRIORITY_COLOR: Record<TicketPriority, string> = {
    1: "#EF4444", 2: "#FB923C", 3: "#9CA3AF",
};
const PRODUCT_COLOR: Record<number, string> = {
    10: "#6366F1", 11: "#8B5CF6", 12: "#EC4899", 14: "#14B8A6",
};
const PRODUCT_NAME: Record<number, string> = {
    10: "mobiOffice", 11: "mobiSales", 12: "mobiShop", 14: "mobiRoute",
};

interface TicketStats {
    total: number;
    byStatus: Record<number, number>;
    byPriority: Record<number, number>;
    byProduct: Record<number, number>;
}
interface AgentRow {
    id: string; name: string;
    open: number; answered: number; pending: number; closed: number; total: number;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function timeAgo(date: string, t: (key: string) => string) {
    const ms = Date.now() - new Date(date).getTime();
    const m = Math.floor(ms / 60000);
    const h = Math.floor(ms / 3600000);
    const d = Math.floor(ms / 86400000);
    if (m < 60) return `${m} ${t("minutesShort")}`;
    if (h < 24) return `${h} ${t("hoursShort")}`;
    return `${d} ${t("daysShort")}`;
}

function pct(part: number, total: number) {
    return total === 0 ? 0 : Math.round((part / total) * 100);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, accent, icon }: {
    label: string; value: number | string; sub?: string;
    accent: string; icon: React.ReactNode;
}) {
    return (
        <div className="dash-card flex items-center gap-3" style={{ padding: 16, borderLeft: `3px solid ${accent}` }}>
            <div
                className="flex items-center justify-center shrink-0"
                style={{ width: 42, height: 42, borderRadius: 10, background: `${accent}1f`, color: accent }}
            >
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-xl font-bold dash-text-primary" style={{ lineHeight: 1.1 }}>{value}</p>
                <p className="text-xs dash-text-muted truncate" style={{ marginTop: 2 }}>{label}</p>
                {sub && <p className="text-xs dash-text-muted truncate" style={{ opacity: 0.7 }}>{sub}</p>}
            </div>
        </div>
    );
}

function SectionCard({ title, badge, children }: { title: string; badge?: string; children: React.ReactNode }) {
    return (
        <div className="dash-card p-5">
            <div className="flex items-center justify-between mb-4">
                <h3 style={{ fontSize: 13, fontWeight: 500, margin: 0, color: 'var(--base-text-color)' }}>{title}</h3>
                {badge && <span className="text-xs dash-text-muted dash-badge px-2 py-0.5 rounded-md">{badge}</span>}
            </div>
            {children}
        </div>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function SupportDashboard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [stats, setStats] = useState<TicketStats | null>(null);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [agents, setAgents] = useState<AgentRow[]>([]);
    const [surveyData, setSurveyData] = useState<{ total: number; responded: number; avgScore: number; distribution: Record<number, number> } | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [ticketStats, allTickets, users, surveyStats] = await Promise.all([
                getTicketStats(), getTickets(), getSupportUsers(), getSurveyStats(),
            ]);
            setSurveyData(surveyStats);

            setStats(ticketStats);
            setTickets(allTickets);

            // agent tablosu
            const map: Record<string, AgentRow> = {};
            users.forEach((u: SupportUser) => {
                map[u.ref] = { id: u.ref, name: u.full_name, open: 0, answered: 0, pending: 0, closed: 0, total: 0 };
            });
            allTickets.forEach((tk: Ticket) => {
                if (tk.assigned_to && map[tk.assigned_to]) {
                    map[tk.assigned_to].total++;
                    if (isClosedStatus(tk.status)) map[tk.assigned_to].closed++;
                    else if (tk.status === 6) map[tk.assigned_to].pending++;     // PENDING
                    else if (tk.status === 4) map[tk.assigned_to].answered++;    // IN_PROGRESS
                    else map[tk.assigned_to].open++;
                }
            });
            setAgents(Object.values(map).filter(a => a.total > 0).sort((a, b) => b.total - a.total));
        } catch {
            notify(t("dashboardLoadError"), "error", 2000);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // ── Derived metrics ──────────────────────────────────────────────────────
    const total       = stats?.total ?? 0;
    const closedCount = TICKET_STATUS_DEFS
        .filter(d => d.isClosed)
        .reduce((n, d) => n + (stats?.byStatus[d.nr] ?? 0), 0);
    const openCount   = total - closedCount;
    const urgentCount = stats?.byPriority[1] ?? 0;
    const resolutionRate = pct(closedCount, total);

    const urgentOpen = tickets.filter(t => t.priority === 1 && !isClosedStatus(t.status));
    const recentTickets = tickets.slice(0, 10);

    // chart data
    const statusChartData = stats
        ? (TICKET_STATUS_DEFS.map(d => d.nr))
            .filter(s => (stats.byStatus[s] ?? 0) > 0)
            .map(s => ({ label: t(STATUS_TEXT_KEYS[s]), count: stats.byStatus[s] ?? 0, color: STATUS_COLOR[s] }))
        : [];

    const productChartData = stats
        ? Object.entries(stats.byProduct).map(([k, v]) => ({
            product: PRODUCT_NAME[Number(k)] ?? t("productLabel", { code: k }),
            count: v,
            color: PRODUCT_COLOR[Number(k)] ?? "#94A3B8",
        }))
        : [];

    return (
        <div className="flex flex-col h-full w-full overflow-auto" style={{ background: 'var(--background-color)' }}>
            <LoadPanel visible={loading} />


            <div className="p-6 space-y-5">

                {/* ── KPI Kartlari ────────────────────────────────────────── */}
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                    <KpiCard
                        label={t("totalTickets")} value={total} sub={t("allTime")}
                        accent="#3B82F6"
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>}
                    />
                    <KpiCard
                        label={t("openTickets")} value={openCount} sub={t("awaitingResponse")}
                        accent="#F97316"
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    />
                    <KpiCard
                        label={t("resolved")} value={closedCount} sub={t("closed")}
                        accent="#16A34A"
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    />
                    <KpiCard
                        label={t("urgent")} value={urgentCount} sub={t("prioritized")}
                        accent="#EF4444"
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
                    />
                    <KpiCard
                        label={t("resolutionRate")} value={`%${resolutionRate}`} sub={t("closedCount", { closed: closedCount, total })}
                        accent="#8B5CF6"
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
                    />
                    <KpiCard
                        label={t("surveyAvgScore")} value={surveyData?.avgScore ?? "—"} sub={t("surveyResponseCount", { responded: surveyData?.responded ?? 0, total: surveyData?.total ?? 0 })}
                        accent="#F59E0B"
                        icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>}
                    />
                </div>

                {/* ── Grafikler satiri ────────────────────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">

                    {/* Durum dagilimi */}
                    <SectionCard title={t("statusDistribution")} badge={t("ticketCount", { count: total })}>
                        <div className="space-y-3">
                            {statusChartData.map(d => (
                                <div key={d.label}>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="dash-text-secondary font-medium">{d.label}</span>
                                        <span className="dash-text-muted">{d.count} · %{pct(d.count, total)}</span>
                                    </div>
                                    <div className="h-2 dash-progress-bg rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all duration-500"
                                            style={{ width: `${pct(d.count, total)}%`, backgroundColor: d.color }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-wrap gap-2 mt-5 pt-4 dash-border-top">
                            {(TICKET_STATUS_DEFS.map(d => d.nr)).map(s => (
                                <div key={s} className="flex items-center gap-1.5">
                                    <span className={`w-2 h-2 rounded-full ${STATUS_BG[s]}`} />
                                    <span className="text-xs dash-text-muted">{t(STATUS_TEXT_KEYS[s])}</span>
                                </div>
                            ))}
                        </div>
                    </SectionCard>

                    {/* Ürün dağılımı */}
                    <SectionCard title={t("productDistribution")} badge={t("module")}>
                        <div className="space-y-3">
                            {productChartData.map(d => (
                                <div key={d.product}>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="dash-text-secondary font-medium">{d.product}</span>
                                        <span className="dash-text-muted">{d.count} · %{pct(d.count, total)}</span>
                                    </div>
                                    <div className="h-2 dash-progress-bg rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all duration-500"
                                            style={{ width: `${pct(d.count, total)}%`, backgroundColor: d.color }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4">
                            <PieChart dataSource={productChartData} size={{ height: 160 }} palette={["#6366F1", "#8B5CF6", "#EC4899", "#14B8A6"]}>
                                <Series argumentField="product" valueField="count">
                                    <Label visible={false} />
                                </Series>
                                <Legend visible verticalAlignment="bottom" horizontalAlignment="center" itemTextPosition="right" font={{ size: 10 }} />
                                <Tooltip enabled customizeTooltip={(a: any) => ({ text: `${a.argumentText}: ${a.valueText}` })} />
                            </PieChart>
                        </div>
                    </SectionCard>

                    {/* Oncelik & Acil */}
                    <SectionCard title={t("priorityAndUrgent")}>
                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {([1, 2, 3] as TicketPriority[]).map(p => (
                                <div
                                    key={p}
                                    className="rounded-lg px-3 py-2.5 flex flex-col items-center justify-center gap-0.5"
                                    style={{ background: `${PRIORITY_COLOR[p]}1a`, color: PRIORITY_COLOR[p] }}
                                >
                                    <span className="text-xl font-bold">{stats?.byPriority[p] ?? 0}</span>
                                    <span className="text-xs font-medium">{t(PRIORITY_TEXT_KEYS[p])}</span>
                                </div>
                            ))}
                        </div>

                        {urgentOpen.length > 0 ? (
                            <>
                                <p className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">
                                    {t("urgentPending", { count: urgentOpen.length })}
                                </p>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {urgentOpen.map(tk => (
                                        <button
                                            key={tk.ref}
                                            onClick={() => navigate(`/support/ticket/${tk.ref}`)}
                                            className="w-full text-left p-2.5 rounded-lg transition-colors"
                                            style={{ background: '#EF44441a', border: '1px solid #EF444433' }}
                                        >
                                            <p className="text-xs font-medium truncate" style={{ color: 'var(--base-text-color)' }}>{tk.title}</p>
                                            <div className="flex items-center gap-1.5 mt-1" style={{ color: '#EF4444' }}>
                                                <span className="font-mono text-xs">{tk.ticket_code}</span>
                                                <span style={{ opacity: 0.5 }}>·</span>
                                                <span className="text-xs">{timeAgo(tk.created_at, t)} {t("ago")}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-6 dash-text-muted">
                                <svg className="w-8 h-8 mb-1 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-xs text-green-600 font-medium">{t("noUrgentTickets")}</p>
                            </div>
                        )}
                    </SectionCard>

                    {/* Memnuniyet Anketi */}
                    <SectionCard title={t("surveyTitle")} badge={`${surveyData?.responded ?? 0} ${t("surveyResponse")}`}>
                        {surveyData && surveyData.responded > 0 ? (
                            <>
                                <div className="flex items-center justify-center gap-2 mb-4">
                                    <span className="text-2xl font-bold" style={{ color: 'var(--base-text-color)' }}>{surveyData.avgScore}</span>
                                    <div className="flex items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <svg key={star} className={`w-5 h-5 ${star <= Math.round(surveyData.avgScore) ? "text-amber-400" : "text-gray-200"}`} fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                            </svg>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {[5, 4, 3, 2, 1].map(star => {
                                        const count = surveyData.distribution[star] ?? 0;
                                        const percent = pct(count, surveyData.responded);
                                        return (
                                            <div key={star} className="flex items-center gap-2 text-xs">
                                                <span className="w-3 dash-text-muted">{star}</span>
                                                <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                </svg>
                                                <div className="flex-1 h-2 dash-progress-bg rounded-full overflow-hidden">
                                                    <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${percent}%` }} />
                                                </div>
                                                <span className="w-8 text-right dash-text-muted">{count}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs dash-text-muted mt-3 text-center">
                                    {t("surveyResponseRate")}: %{pct(surveyData.responded, surveyData.total)}
                                </p>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-6 dash-text-muted">
                                <svg className="w-8 h-8 mb-1 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                                <p className="text-xs font-medium">{t("noSurveyData")}</p>
                            </div>
                        )}
                    </SectionCard>
                </div>

                {/* ── Alt satir ───────────────────────────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

                    {/* Ekip performansi */}
                    <div className="lg:col-span-2">
                        <SectionCard title={t("teamPerformance")} badge={t("staff")}>
                            {agents.length === 0 ? (
                                <div className="text-center py-8 dash-text-muted text-sm">{t("noAssignedTickets")}</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="dash-text-muted dash-border-bottom">
                                                <th className="text-left pb-2 font-medium">{t("staff")}</th>
                                                <th className="text-center pb-2 font-medium">{t("open")}</th>
                                                <th className="text-center pb-2 font-medium">{t("answered")}</th>
                                                <th className="text-center pb-2 font-medium">{t("closed")}</th>
                                                <th className="text-right pb-2 font-medium">{t("totalLabel")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {agents.map(a => {
                                                const closedPct = pct(a.closed, a.total);
                                                return (
                                                    <tr key={a.id} className="dash-row-hover transition-colors">
                                                        <td className="py-2.5">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0" style={{ background: 'var(--accent-color)' }}>
                                                                    {a.name.charAt(0)}
                                                                </div>
                                                                <div>
                                                                    <p className="font-medium dash-text-primary">{a.name}</p>
                                                                    <div className="w-20 h-1 dash-progress-bg rounded-full mt-1">
                                                                        <div className="h-full bg-green-400 rounded-full" style={{ width: `${closedPct}%` }} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center py-2.5 font-semibold text-orange-500">{a.open}</td>
                                                        <td className="text-center py-2.5 font-semibold text-blue-500">{a.answered}</td>
                                                        <td className="text-center py-2.5 font-semibold text-green-600">{a.closed}</td>
                                                        <td className="text-right py-2.5 font-bold dash-text-secondary">{a.total}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Son ticketlar */}
                    <div className="lg:col-span-3">
                        <SectionCard title={t("recentTickets")} badge={t("lastCount", { count: recentTickets.length })}>
                            <div className="space-y-1.5 max-h-80 overflow-y-auto">
                                {recentTickets.map(tk => (
                                    <button
                                        key={tk.ref}
                                        onClick={() => navigate(`/support/ticket/${tk.ref}`)}
                                        className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl dash-row-hover transition-colors group"
                                    >
                                        <div
                                            className="w-1 self-stretch rounded-full shrink-0"
                                            style={{ backgroundColor: PRIORITY_COLOR[tk.priority] }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-xs dash-text-muted">{tk.ticket_code}</span>
                                            </div>
                                            <p className="text-sm font-medium dash-text-primary truncate group-hover:text-blue-600 transition-colors">
                                                {tk.title}
                                            </p>
                                            <p className="text-xs dash-text-muted mt-0.5">{tk.customer_name} · {timeAgo(tk.created_at, t)} {t("ago")}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <span
                                                className="text-xs px-2 py-0.5 rounded-full font-medium"
                                                style={{ backgroundColor: STATUS_COLOR[tk.status] + "20", color: STATUS_COLOR[tk.status] }}
                                            >
                                                {t(STATUS_TEXT_KEYS[tk.status])}
                                            </span>
                                            {tk.assigned_to_name && (
                                                <span className="text-xs dash-text-muted">{tk.assigned_to_name.split(" ")[0]}</span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </SectionCard>
                    </div>
                </div>

                {/* ── Ozet satiri ─────────────────────────────────────────── */}
                <div className="dash-card">
                    <div className="grid grid-cols-2 md:grid-cols-5 dash-divide-x">
                        {[
                            { label: t("statusNew"),             value: stats?.byStatus[1] ?? 0, color: "text-blue-600" },
                            { label: t("statusInProgress"),      value: stats?.byStatus[4] ?? 0, color: "text-green-600" },
                            { label: t("statusWaitingCustomer"), value: stats?.byStatus[5] ?? 0, color: "text-orange-500" },
                            { label: t("statusPending"),         value: stats?.byStatus[6] ?? 0, color: "text-yellow-500" },
                            { label: t("activeStaff"),       value: agents.length,            color: "text-purple-600" },
                        ].map(m => (
                            <div key={m.label} className="py-4 px-5 text-center">
                                <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
                                <p className="text-xs dash-text-muted mt-1">{m.label}</p>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
}
